<template>
    <div
        class="selected-badge"
        :class="{ 'search-badge': isSearchBadge, 'plain-text': plainText }"
    >
        <span class="badge-text">{{ text }}</span>
        <span v-if="removable" class="badge-remove-container">
            <i class="fi fi-x badge-remove" @click.stop="$emit('remove')"></i>
        </span>
    </div>
</template>

<script>
/**
 * @component SelectedBadge
 * @description A reusable badge component for displaying selected options
 */
export default {
    name: 'SelectedBadge',
    props: {
        /**
         * @prop {String} text - The text to display in the badge
         */
        text: {
            type: String,
            required: true,
        },
        /**
         * @prop {Boolean} removable - Whether the badge can be removed
         */
        removable: {
            type: Boolean,
            default: false,
        },
        /**
         * @prop {Boolean} isSearchBadge - Whether the badge is used in search input
         */
        isSearchBadge: {
            type: Boolean,
            default: false,
        },
        /**
         * @prop {Boolean} plainText - Whether to render as plain text (no badge styling)
         */
        plainText: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['remove'],
};
</script>

<style scoped>
.selected-badge {
    background-color: var(--success);
    color: white;
    border-radius: var(--radius-lg);
    padding: var(--space-1) var(--space-2);
    font-size: 0.875rem;
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    height: 1.75rem;
}

.badge-text {
    font-size: var(--size-3);
    font-weight: 600;
    line-height: 1.67;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 9.375rem;
}

.badge-remove-container {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.125rem;
    border-radius: 50%;
    transition: background-color 0.2s ease;
}

.badge-remove-container:hover {
    background-color: var(--neutral-450);
}

.badge-remove {
    font-size: 0.875rem;
    color: var(--black);
    cursor: pointer;
    opacity: 0.7;
    transition: all 0.2s ease;
    width: 0.875rem;
    height: 0.875rem;
    display: flex;
    align-items: center;
    justify-content: center;
}

.badge-remove:hover {
    opacity: 1;
    transform: scale(1.1);
}

.search-badge .badge-remove {
    font-size: 0.625rem;
    width: 0.625rem;
    height: 0.625rem;
}

/* Plain text mode - no badge styling */
.plain-text {
    background-color: transparent;
    color: var(--neutral-800);
    border: none;
    padding: 0;
    border-radius: 0;
    font-weight: 400;
}

.plain-text .badge-text {
    font-weight: 400;
    font-size: inherit;
    line-height: inherit;
}
</style>
